import { I18nService } from '../../shared/i18n/i18n.service';
import { DashboardService } from '../../features/dashboard/services/dashboard/dashboard.service';
import { CustomViewsService } from '../../features/custom-views/services/custom-views.service';
import { COMMAND_PALETTE_PAGES } from './command-palette-pages';

const DASHBOARD_PATH = /^\/dashboard\/([^/?#]+)/;
const CUSTOM_VIEW_PATH = /^\/custom-views\/([^/?#]+)/;

/** The lists a tab title may name an item from. */
export interface TabTitleSources {
  dashboards: Pick<DashboardService, 'dashboardList' | 'allDashboardList'>;
  customViews: Pick<CustomViewsService, 'viewList'>;
}

/**
 * A tab's title for a bare in-app path: its command-palette page's label, a
 * dashboard's or custom view's name, or else the path itself — a url carries no title of its
 * own. Dashboards are looked up in the project's list and in the admin
 * "All dashboards" one, since either can have opened the page. Shared by
 * `~usetabs` and the Ctrl+Alt+P "keep this page as a tab" shortcut. A label whose namespace isn't loaded yet falls back to the path,
 * since `I18nService.t()` throws for a scope still loading.
 */
export function tabTitleFor(url: string, i18n: I18nService, sources: TabTitleSources): string {
  return resolveTabTitle(url, i18n, sources) ?? url;
}

/**
 * {@link tabTitleFor} without its fallback: null when the url names no known
 * page, dashboard or custom view (or its label's namespace is still loading).
 */
export function resolveTabTitle(url: string, i18n: I18nService, sources: TabTitleSources): string | null {
  const path = url.split(/[?#]/)[0];
  const page = COMMAND_PALETTE_PAGES.find((p) => p.route === path);
  if (page && i18n.getScopeReady(page.namespace)()) {
    return i18n.t(page.labelKey, page.namespace);
  }
  const dashboardId = DASHBOARD_PATH.exec(url)?.[1];
  if (dashboardId) {
    const { dashboardList, allDashboardList } = sources.dashboards;
    const dashboard = [...dashboardList(), ...allDashboardList()].find((d) => d.id === dashboardId);
    if (dashboard) return dashboard.name;
  }
  const viewId = CUSTOM_VIEW_PATH.exec(url)?.[1];
  if (!viewId) return null;
  return sources.customViews.viewList().find((v) => v.id === viewId)?.name ?? null;
}
